import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSignUp, useAuth, useClerk } from '@clerk/clerk-react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { StepProps } from '../types/stepTypes';
import { completeUserOnboarding, checkSubscriptionByPhone } from '../../api-v2';
import { useOnboardingDataRedux } from '../../hooks/useOnboardingDataRedux';
import { sendOtp, verifyOtp } from '../../api';
import { formatPhoneNumber } from '../../api-v2';

const CompletionStep: React.FC<StepProps> = ({
  currentStep,
  setCurrentStep,
  completedSteps,
  setCompletedSteps,
  selectedPlan,
  onClose,
  userEmail,
  userPhoneNumber,
  setUserPhoneNumber
}) => {
  const navigate = useNavigate();
  const { data: onboardingData, updateData, resetData, completeOnboarding, paymentData, hasPaymentCompleted, restoreFromBackup } = useOnboardingDataRedux();
  const { isLoaded, signUp } = useSignUp();
  const { isSignedIn, signOut } = useAuth();
  const clerk = useClerk();

  // Phone verification states - Use Redux for persistent data, local state for UI only
  const [verificationStep, setVerificationStep] = useState<'phone' | 'otp'>('phone');
  const phoneNumber = onboardingData.phoneNumber || userPhoneNumber || '';
  const smsConsent = onboardingData.smsConsent || false;
  
  const [otp, setOtp] = useState('');
  const [isPhoneFocused, setIsPhoneFocused] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [otpSentAt, setOtpSentAt] = useState<number | null>(null);
  const [isVerified, setIsVerified] = useState(false);

  // Helper functions to update Redux state directly
  const setPhoneNumber = (value: string) => updateData({ phoneNumber: value });
  const setSmsConsent = (value: boolean) => updateData({ smsConsent: value });

  // Auto-restore from backup and set verification state
  useEffect(() => {
    // Auto-restore from backup if we should have data but don't
    const shouldHaveData = onboardingData.currentStep === 3 && 
                          onboardingData.paymentStatus === 'completed' && 
                          !onboardingData.phoneNumber;
    
    if (shouldHaveData) {
      console.log('🔄 CompletionStep: Auto-restoring from backup');
      restoreFromBackup();
      return;
    }
    
    // If user already has verified phone, set verification state
    if (onboardingData.phoneNumber && onboardingData.smsConsent) {
      setIsVerified(true);
    }
  }, [onboardingData.phoneNumber, onboardingData.smsConsent, onboardingData.currentStep, onboardingData.paymentStatus, restoreFromBackup]);

  // Only show completion step if we're on step 3 (phone verification step)
  if (currentStep !== 3) return null;

  // Phone verification handlers  
  const handlePhoneChange = (value: string, country: any) => {
    // The value from react-phone-input-2 already includes the country code
    // Format: value will be like "18144338487", we need to add the + prefix
    const formattedNumber = value.startsWith('+') ? value : `+${value}`;
    setPhoneNumber(formattedNumber);
    setErrorMessage(''); // Clear error when user types
  };

  const handlePhoneSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (phoneNumber.trim() && phoneNumber.length > 8) {
      // Prevent multiple rapid submissions
      const now = Date.now();
      if (otpSentAt && (now - otpSentAt) < 30000) { // 30 seconds cooldown
        setErrorMessage('Please wait 30 seconds before sending another OTP.');
        return;
      }

      setIsLoading(true);
      setErrorMessage('');
      setSuccessMessage('');

      try {
        // Validate phone number is not already in use before sending OTP
        console.log('🚨 CHECKING PHONE:', phoneNumber);
        const phoneCheckResponse = await checkSubscriptionByPhone(phoneNumber);
        console.log('🚨 API RESPONSE:', JSON.stringify(phoneCheckResponse, null, 2));
        
        // If a user exists with this phone number (regardless of subscription status)
        // or if there's an active subscription, block the signup
        if (phoneCheckResponse.success && phoneCheckResponse.user) {
          console.log('🚨 BLOCKING SIGNUP - User found:', phoneCheckResponse.user);
          setErrorMessage('Please use another number. This phone number is already being used by another account.');
          setIsLoading(false);
          return;
        }
        
        console.log('🚨 ALLOWING SIGNUP - No user found or API failed');

        // Send OTP using original phone number format
        const response = await sendOtp(phoneNumber);

        if (response.success) {
          setSuccessMessage('OTP sent successfully!');
          setVerificationStep('otp');
          setOtpSentAt(Date.now());
        } else {
          setErrorMessage(response.message || 'Failed to send OTP');
        }
      } catch (error: any) {
        setErrorMessage(error.message || 'Failed to send OTP. Please try again.');
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleOtpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (otp.length === 6) {
      setIsLoading(true);
      setErrorMessage('');
      setSuccessMessage('');

      try {
        // Phone number is already formatted with country code from handlePhoneChange
        const response = await verifyOtp(phoneNumber, otp);

        if (response.success) {
          setSuccessMessage('Phone number verified successfully!');
          setUserPhoneNumber?.(phoneNumber);
          setIsVerified(true);
          
          // Store the JWT tokens for authentication but don't trigger auth update yet
          if (response.accessToken && response.refreshToken) {
            localStorage.setItem('accessToken', response.accessToken);
            localStorage.setItem('refreshToken', response.refreshToken);
            console.log('🔑 Phone verification tokens stored');
          }
          if (response.user?.phone) {
            localStorage.setItem('userPhone', response.user.phone);
          }
          
          localStorage.setItem('phoneVerified', 'true');
          
        } else {
          setErrorMessage(response.message || 'Invalid OTP');
        }
      } catch (error: any) {
        setErrorMessage(error.message || 'Failed to verify OTP. Please try again.');
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleBackToPhone = () => {
    setVerificationStep('phone');
    setOtp('');
    setErrorMessage('');
    setSuccessMessage('');
  };

    // Handle phone verification and proceed to payment step
  const handleProceedToPayment = async () => {
    if (!isVerified) {
      setErrorMessage('Please verify your phone number first.');
      return;
    }
    
    if (!smsConsent) {
      setErrorMessage('You must agree to receive SMS messages to continue.');
      return;
    }
    
    setErrorMessage('');
    setIsLoading(true);
    
    try {
      // Format phone number for consistency
      const formattedPhone = formatPhoneNumber(phoneNumber);

      // Save phone number to parent component and Redux
      setUserPhoneNumber?.(formattedPhone);
      
      // Update Redux with phone verification data
      updateData({
        phoneNumber: formattedPhone,
        smsConsent: true
      });

      console.log('✅ Phone verification data saved, proceeding to payment step');
      
      // Proceed to payment step (step 4)
      const newCompletedSteps = [...completedSteps];
      newCompletedSteps[2] = true; // Mark step 3 (phone verification) as completed
      setCompletedSteps(newCompletedSteps);
      setCurrentStep?.(4); // Go to payment step
      
    } catch (error: any) {
      console.error('❌ Error updating phone verification data:', error);
      setErrorMessage('Failed to save phone verification. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Helper function to handle session conflicts
  const handleSessionConflict = () => {
    // Clear any local storage or session storage related to Clerk
    if (typeof window !== 'undefined') {
      // Clear Clerk-related items from localStorage
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('clerk') || key.startsWith('__clerk')) {
          localStorage.removeItem(key);
        }
      });
      
      // Clear Clerk-related items from sessionStorage
      Object.keys(sessionStorage).forEach(key => {
        if (key.startsWith('clerk') || key.startsWith('__clerk')) {
          sessionStorage.removeItem(key);
        }
      });
      
      // Clear Redux persist state as well
      localStorage.removeItem('persist:silas_root');
    }
    
    // Reset onboarding data
    resetData();
    
    // Refresh the page to reset Clerk state
    window.location.reload();
  };

  return (
    <div className="w-full max-w-md mx-auto">
      <div className="text-center mb-4">
        <h2 className="text-xl font-semibold text-gray-900">
          Your Subscription
        </h2>
      </div>

      {/* Error/Success Messages */}
      {errorMessage && (
        <div className="text-sm text-red-600 bg-red-50 rounded-lg p-2 mt-2 mb-4">
          {errorMessage}
        </div>
      )}
      {successMessage && (
        <div className="text-sm text-green-600 bg-green-50 rounded-lg p-2 mt-2 mb-4">
          {successMessage}
        </div>
      )}

      {/* Step 1: Phone Verification */}
      {!isVerified && (
        <div>
          {verificationStep === 'phone' ? (
            <form onSubmit={handlePhoneSubmit} className="space-y-4">
              <div>
                <label htmlFor="phoneNumber" className="block text-xs font-medium text-gray-700 mb-1">
          Phone Number <span className="text-red-500">*</span>
        </label>
                <PhoneInput
                  country={'us'}
          value={phoneNumber}
                  onChange={handlePhoneChange}
                  onFocus={() => setIsPhoneFocused(true)}
                  onBlur={() => setIsPhoneFocused(false)}
                  placeholder="Enter your phone number"
                  enableSearch={true}
                  searchPlaceholder="Search countries..."
                  searchNotFound="No countries found"
                  preferredCountries={['us', 'gb', 'ca', 'au', 'de', 'fr', 'in', 'jp']}
                  disableCountryCode={false}
                  countryCodeEditable={false}
                  autoFormat={true}
                  specialLabel=""
                  containerStyle={{
                    width: '100%',
                    height: '48px'
                  }}
                  inputStyle={{
                    width: '100%',
                    height: '48px',
                    fontSize: '16px',
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                    border: isPhoneFocused ? '2px solid #667eea' : '2px solid #e2e8f0',
                    borderRadius: '12px',
                    paddingLeft: '60px',
                    paddingRight: '16px',
                    color: '#2d3748',
                    backgroundColor: 'white',
                    transition: 'all 0.2s ease',
                    boxShadow: isPhoneFocused 
                      ? '0 0 0 3px rgba(102, 126, 234, 0.1), 0 2px 4px rgba(0, 0, 0, 0.05)' 
                      : '0 2px 4px rgba(0, 0, 0, 0.05)',
                    outline: 'none'
                  }}
                  buttonStyle={{
                    backgroundColor: '#f8fafc',
                    border: 'none',
                    borderRight: '1px solid #e2e8f0',
                    borderTopLeftRadius: '10px',
                    borderBottomLeftRadius: '10px',
                    padding: '0px 0px',
                    minWidth: '40px',
                    height: '48px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    alignSelf:'center',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  dropdownStyle={{
                    position: 'absolute',
                    top: '100%',
                    left: '0',
                    right: '0',
                    backgroundColor: 'white',
                    border: '2px solid #e2e8f0',
                    borderTop: 'none',
                    borderBottomLeftRadius: '12px',
                    borderBottomRightRadius: '12px',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
                    zIndex: 9999,
                    maxHeight: '200px',
                    overflowY: 'auto'
                  }}
                />
          </div>

              <button
                type="submit"
                disabled={!phoneNumber.trim() || phoneNumber.length < 8 || isLoading}
                className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-sm font-medium py-2.5 rounded-lg hover:from-blue-700 hover:to-indigo-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Sending...' : 'Send Verification Code'}
              </button>
            </form>
          ) : (
            <div className="space-y-4">
              <div className="text-center">
                <p className="text-sm text-gray-600 mb-4">
                  We sent a verification code to <span className="font-medium">{phoneNumber}</span>
        </p>
      </div>

                             <form onSubmit={handleOtpSubmit} className="space-y-4">
                 <div>
                   <label htmlFor="otp" className="block text-xs font-medium text-gray-700 mb-1">
                     Verification Code
                   </label>
                   <p className="text-xs text-gray-500 mb-4">
                     We've sent a 6-digit code to {phoneNumber}
                   </p>
                   <div className="flex gap-2 justify-center mb-4">
                     {[0, 1, 2, 3, 4, 5].map((index) => (
                       <input
                         key={index}
                         type="text"
                         inputMode="numeric"
                         pattern="[0-9]*"
                         maxLength={1}
                         value={otp[index] || ''}
                         className="w-12 h-12 text-center text-lg font-semibold border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                         onChange={(e) => {
                           // Only allow numeric characters
                           const value = e.target.value.replace(/[^0-9]/g, '');
                           const newOtp = otp.split('');
                           newOtp[index] = value;
                           setOtp(newOtp.join(''));
                           setErrorMessage('');
                           
                           // Auto-focus next input
                           if (value && index < 5) {
                             const nextInput = (e.target as HTMLInputElement).parentElement?.children[index + 1] as HTMLInputElement;
                             nextInput?.focus();
                           }
                         }}
                         onKeyDown={(e) => {
                           // Handle backspace
                           if (e.key === 'Backspace' && !otp[index] && index > 0) {
                             const prevInput = (e.target as HTMLInputElement).parentElement?.children[index - 1] as HTMLInputElement;
                             prevInput?.focus();
                           }
                         }}
                       />
                     ))}
                   </div>
                 </div>

                 <button
                   type="submit"
                   disabled={otp.length !== 6 || isLoading}
                   className={`w-full text-white text-base font-medium py-3.5 rounded-lg transition-all duration-200 ${
                     (otp.length !== 6 || isLoading)
                       ? 'bg-[#93B4F8] opacity-70'
                       : 'bg-gradient-to-r from-[#6366F1] to-[#8B5CF6] hover:from-[#5558E8] hover:to-[#7C4DEF] shadow-sm'
                   }`}
                   style={{
                     cursor: (otp.length !== 6 || isLoading) ? 'not-allowed' : 'pointer'
                   }}
                 >
                   {isLoading ? 'Verifying...' : 'Continue'}
                 </button>
               </form>

              <button
                onClick={handleBackToPhone}
                className="w-full text-gray-500 text-xs hover:text-gray-700 transition-colors"
                disabled={isLoading}
              >
                ← Change phone number
              </button>
            </div>
          )}
        </div>
      )}

      {/* Step 2: SMS Consent and Continue to Payment */}
      {isVerified && (
        <div>
          {/* SMS Consent Section */}
          <div className="mb-4 bg-blue-50 p-3 rounded-lg border border-blue-100">
            <h3 className="font-medium text-gray-900 mb-1 text-sm">SMS Consent</h3>
            
            <div className="text-xs text-gray-700 leading-tight mb-2">
              By submitting this form, you consent to receive recurring text messages from Silas AI Assistant related to healthcare services and support. You may receive up to 15 messages/month (depending on your plan). Msg & data rates may apply.
            </div>
            
            <div className="text-xs text-gray-700 leading-tight mb-1">
              <span className="font-semibold">Sending number:</span> +1 (814) 433-8487
            </div>
            
            <div className="text-xs text-gray-700 leading-tight mb-2">
              Text <span className="font-semibold">START</span> to opt in, <span className="font-semibold">STOP</span> to unsubscribe, <span className="font-semibold">HELP</span> for help.
            </div>
            
            <div className="flex items-start space-x-3">
              <input
                id="smsConsent"
                type="checkbox"
                checked={smsConsent}
                onChange={(e) => {
                  setSmsConsent(e.target.checked);
                  setErrorMessage('');
                }}
                className="mt-0.5 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                disabled={isLoading}
              />
              <label htmlFor="smsConsent" className="text-xs font-medium text-gray-700">
                I agree to the SMS terms described above
              </label>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2 mb-4">
            <div className="bg-white rounded-lg p-2 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex flex-col items-center text-center">
                <div className="w-7 h-7 bg-blue-100 rounded-full flex items-center justify-center mb-1">
              <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            </div>
                <h3 className="font-medium text-xs text-gray-900 mb-0.5">Start Chatting</h3>
            <p className="text-xs text-gray-500">Begin your journey</p>
          </div>
        </div>

            <div className="bg-white rounded-lg p-2 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex flex-col items-center text-center">
                <div className="w-7 h-7 bg-indigo-100 rounded-full flex items-center justify-center mb-1">
              <svg className="w-4 h-4 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
                <h3 className="font-medium text-xs text-gray-900 mb-0.5">Set Goals</h3>
                <p className="text-xs text-gray-500">Track progress</p>
          </div>
        </div>
      </div>

          <button
            onClick={handleProceedToPayment}
            disabled={isLoading || !smsConsent}
            className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-sm font-medium py-2.5 rounded-lg hover:from-blue-700 hover:to-indigo-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Verifying...' : 'Continue to Payment →'}
          </button>
        </div>
      )}

      {/* Show reset button if there's a session conflict error */}
      {errorMessage && (errorMessage.includes('session') || errorMessage.includes('Session') || errorMessage.includes('signed in')) && (
        <button
          onClick={handleSessionConflict}
          className="w-full mt-3 bg-red-500 text-white text-sm font-medium py-2.5 rounded-lg hover:bg-red-600 focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-all duration-200"
        >
          Reset Session & Try Again
        </button>
      )}

      {/* Consistent Back/Continue button layout */}
      <div className="flex justify-between items-center mt-8 mb-4">
        <button
          onClick={() => setCurrentStep?.(2)}
          className="px-6 py-3 text-sm border border-gray-300 text-gray-600 rounded-lg font-medium 
                     hover:border-gray-400 hover:bg-gray-50 transition-all duration-300 
                     flex items-center gap-2 group"
        >
          <span className="transform group-hover:-translate-x-1 transition-transform duration-300">←</span>
          Back
        </button>
        {isVerified && smsConsent && (
          <button
            onClick={handleProceedToPayment}
            disabled={isLoading || !smsConsent}
            className={`px-6 py-3 rounded-lg text-sm font-medium transition-all duration-300 
                       flex items-center gap-2 group ${
              (!isLoading && smsConsent)
                ? 'bg-black text-white hover:bg-gray-800 shadow-md hover:shadow-lg'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
          >
            {isLoading ? 'Verifying...' : 'Continue to Payment'}
            <span className="transform group-hover:translate-x-1 transition-transform duration-300">→</span>
          </button>
        )}
      </div>
    </div>
  );
};

export default CompletionStep; 